/**
 * API endpoint constants
 * Centralized location for all API routes to maintain consistency
 */

export const API_BASE_URL = import.meta.env.VITE_API_URL as string;

export const ENDPOINTS = {
  // Organizations
  ORGANIZATIONS: {
    MY: '/my/organizations',
    CREATE: '/organizations',
    DETAILS: (orgId: string) => `/organizations/${orgId}`,
    MEMBERS: (orgId: string) => `/organizations/${orgId}/members`,
    SUBSCRIPTION: (orgId: string) => `/organizations/${orgId}/subscription`,
    USAGE: (orgId: string) => `/organizations/${orgId}/usage`,
    REQUEST_PLAN: (orgId: string) => `/organizations/${orgId}/subscription/request`,
    BILLING_EVENTS: (orgId: string) => `/organizations/${orgId}/billing-events`,
    AI_SETTINGS: (orgId: string) => `/organizations/${orgId}/ai-settings`,
  },

  // AI
  AI: {
    SUMMARIZE: (projectId: string) => `/projects/${projectId}/ai/summarize`,
    SEARCH: (projectId: string) => `/projects/${projectId}/ai/search`,
    CREATE_ISSUE: (projectId: string) => `/projects/${projectId}/ai/create-issue`,
  },

  // Authentication
  AUTH: {
    LOGIN: '/login',
    SIGNUP: '/users/signup',
    SIGNUP_SEND_OTP: '/users/signup/send-otp',
    SIGNUP_VERIFY_OTP: '/users/signup/verify-otp',
    FORGOT_PASSWORD: '/users/reset',
    RESET_PASSWORD: '/users/confirmreset',
  },

  // Users
  USERS: {
    BASE: '/users',
    PROFILE_UPDATE: '/users/profile/update/',
    INVITE: '/users/invite',
    CHANGE_PASSWORD: '/users/change',
  },

  // Groups
  GROUPS: {
    BASE: '/groups',
    LIST: '/groups',  // Backend uses /groups not /groups/list
    DETAILS: (groupId: number) => `/groups/${groupId}`,
    MEMBERS: (groupId: number) => `/groups/${groupId}/members`,
    ADD_MEMBER: '/groups/members',
    DELETE_MEMBER: (groupId: number, memberId: number) => `/groups/${groupId}/members/${memberId}`,
    LEAVE: (groupId: number) => `/groups/leave/${groupId}`,
    FAVORITE: '/groups/fav/add',
    INVITATIONS: {
      RECEIVED: '/groups/received/invitations',
      COUNT: '/groups/invitation/count',
      ACCEPT: (invitationId: number) => `/groups/invitations/accept/${invitationId}`,
      REJECT: (invitationId: number) => `/groups/invitations/reject/${invitationId}`,
    },
    MAKE_ADMIN: (groupId: number, memberId: number) => `/groups/${groupId}/members/${memberId}/admin`,
  },

  // Tasks
  TASKS: {
    BASE: '/task',
    CREATE: '/task/create',
    LIST: (groupId: number) => `/task/list/${groupId}`,
    DETAILS: (taskId: number) => `/task/${taskId}`,
    UPDATE: (taskId: number) => `/task/update/${taskId}`,
    UPDATE_STATUS: (taskId: number) => `/task/changestatus/${taskId}`,
    UPDATE_PRIORITY: (taskId: number) => `/task/changestatus/taskpriority/${taskId}`,
    CLOSE: (taskId: number) => `/task/group/status/change/${taskId}`,
    CLOSED_LIST: (groupId: number) => `/task/list/${groupId}`,
    FILTER: '/task/filter/list',
    DRAG_DROP: '/task/create/neworder/dropposition',
    REMINDER: (taskId: number) => `/task/group/members/reminder/${taskId}`,
  },

  // Master Tasks (Recurring)
  MASTER_TASKS: {
    BASE: '/mastertask',
    LIST: (groupId: number) => `/mastertask/group/${groupId}`,
    DETAILS: (taskId: number) => `/mastertask/${taskId}`,
  },

  // Comments
  COMMENTS: {
    BASE: '/comment/addcomment',
    LIST: (taskId: number) => `/comment/getcomments/${taskId}`,
    DELETE: (commentId: number) => `/comment/${commentId}`,
  },

  // Notifications
  NOTIFICATIONS: {
    BASE: '/notification',
    LIST: '/notification',
    COUNT: '/notification/new/count',
    MARK_READ: (notificationId: number) => `/notification/${notificationId}`,
    MARK_ALL_READ: '/notification/status/all/',
  },

  // Frontend/Public
  FRONT: {
    CONTACT_US: '/front/contactUS',
  },

  // Workspaces
  WORKSPACES: {
    BASE: '/workspaces',
    LIST: '/workspaces',
    DETAILS: (workspaceId: string) => `/workspaces/${workspaceId}`,
    BY_SLUG: (slug: string) => `/workspaces/slug/${slug}`,
    MEMBERS: (workspaceId: string) => `/workspaces/${workspaceId}/members`,
    MEMBER: (workspaceId: string, userId: string) => `/workspaces/${workspaceId}/members/${userId}`,
    LEAVE: (workspaceId: string) => `/workspaces/${workspaceId}/leave`,
    PROJECTS: (workspaceId: string) => `/workspaces/${workspaceId}/projects`,
    DASHBOARD: (workspaceId: string) => `/workspaces/${workspaceId}/dashboard`,
    INVITATIONS: {
      LIST: (workspaceId: string) => `/workspaces/${workspaceId}/invitations`,
      RECEIVED: '/workspaces/invitations/received',
      ACCEPT: (invitationId: string) => `/workspaces/invitations/${invitationId}/accept`,
      REJECT: (invitationId: string) => `/workspaces/invitations/${invitationId}/reject`,
      CANCEL: (invitationId: string) => `/workspaces/invitations/${invitationId}`,
      ACCEPT_BY_CODE: '/workspaces/invitation/accept',
      REJECT_BY_CODE: '/workspaces/invitation/reject',
    },
  },

  // Issue Types
  ISSUE_TYPES: {
    LIST: (workspaceId: string) => `/workspaces/${workspaceId}/issue-types`,
    DEFAULT: (workspaceId: string) => `/workspaces/${workspaceId}/issue-types/default`,
    CREATE_DEFAULTS: (workspaceId: string) => `/workspaces/${workspaceId}/issue-types/defaults`,
    REORDER: (workspaceId: string) => `/workspaces/${workspaceId}/issue-types/reorder`,
    SET_DEFAULT: (workspaceId: string, issueTypeId: string) => `/workspaces/${workspaceId}/issue-types/${issueTypeId}/set-default`,
    DETAILS: (issueTypeId: string) => `/issue-types/${issueTypeId}`,
  },

  // Workflow Statuses
  WORKFLOW_STATUSES: {
    LIST: (workspaceId: string) => `/workspaces/${workspaceId}/workflow-statuses`,
    DEFAULT: (workspaceId: string) => `/workspaces/${workspaceId}/workflow-statuses/default`,
    DONE: (workspaceId: string) => `/workspaces/${workspaceId}/workflow-statuses/done`,
    BY_CATEGORY: (workspaceId: string, category: string) => `/workspaces/${workspaceId}/workflow-statuses/category/${category}`,
    CREATE_DEFAULTS: (workspaceId: string) => `/workspaces/${workspaceId}/workflow-statuses/defaults`,
    REORDER: (workspaceId: string) => `/workspaces/${workspaceId}/workflow-statuses/reorder`,
    SET_DEFAULT: (workspaceId: string, workflowStatusId: string) => `/workspaces/${workspaceId}/workflow-statuses/${workflowStatusId}/set-default`,
    DETAILS: (workflowStatusId: string) => `/workflow-statuses/${workflowStatusId}`,
  },

  // Sprints
  SPRINTS: {
    LIST: (projectId: string) => `/projects/${projectId}/sprints`,
    ACTIVE: (projectId: string) => `/projects/${projectId}/sprints/active`,
    FUTURE: (projectId: string) => `/projects/${projectId}/sprints/future`,
    CLOSED: (projectId: string) => `/projects/${projectId}/sprints/closed`,
    REORDER: (projectId: string) => `/projects/${projectId}/sprints/reorder`,
    DETAILS: (sprintId: string) => `/sprints/${sprintId}`,
    STATS: (sprintId: string) => `/sprints/${sprintId}/stats`,
    ISSUES: (sprintId: string) => `/sprints/${sprintId}/issues`,
    START: (sprintId: string) => `/sprints/${sprintId}/start`,
    COMPLETE: (sprintId: string) => `/sprints/${sprintId}/complete`,
    ADD_ISSUES: (sprintId: string) => `/sprints/${sprintId}/issues`,
    REMOVE_ISSUES: (sprintId: string) => `/sprints/${sprintId}/issues`,
  },

  // Board
  BOARD: {
    DATA: (projectId: string) => `/projects/${projectId}/board`,
    COLUMNS: (projectId: string) => `/projects/${projectId}/board/columns`,
    COLUMN: (columnId: string) => `/board/columns/${columnId}`,
    REORDER_COLUMNS: (projectId: string) => `/projects/${projectId}/board/columns/reorder`,
    CREATE_DEFAULT_COLUMNS: (projectId: string) => `/projects/${projectId}/board/columns/default`,
    MOVE_ISSUE: (taskId: string) => `/board/issues/${taskId}/move`,
    REORDER_ISSUES: (workflowStatusId: string) => `/board/columns/${workflowStatusId}/issues/reorder`,
    BACKLOG: (projectId: string) => `/projects/${projectId}/backlog`,
    REORDER_BACKLOG: (projectId: string) => `/projects/${projectId}/backlog/reorder`,
  },

  // Dependencies
  DEPENDENCIES: {
    ALL: (taskId: string) => `/issues/${taskId}/dependencies`,
    BLOCKING: (taskId: string) => `/issues/${taskId}/dependencies/blocking`,
    BLOCKED_BY: (taskId: string) => `/issues/${taskId}/dependencies/blocked-by`,
    PROJECT: (projectId: string) => `/projects/${projectId}/dependencies`,
    DETAILS: (dependencyId: string) => `/dependencies/${dependencyId}`,
    VALIDATE: '/dependencies/validate',
    CHAIN: (taskId: string) => `/issues/${taskId}/dependency-chain`,
  },

  // Timeline
  TIMELINE: {
    DATA: (projectId: string) => `/projects/${projectId}/timeline`,
    TASKS: (projectId: string) => `/projects/${projectId}/timeline/tasks`,
    UPDATE_TASK: (taskId: string) => `/timeline/issues/${taskId}`,
    BATCH_UPDATE: (projectId: string) => `/projects/${projectId}/timeline/batch`,
    CRITICAL_PATH: (projectId: string) => `/projects/${projectId}/timeline/critical-path`,
    AFFECTED: (taskId: string) => `/timeline/issues/${taskId}/affected`,
    DATE_RANGE: (projectId: string) => `/projects/${projectId}/timeline/date-range`,
    UNSCHEDULED: (projectId: string) => `/projects/${projectId}/timeline/unscheduled`,
    OVERDUE: (projectId: string) => `/projects/${projectId}/timeline/overdue`,
  },

  // Custom Fields
  CUSTOM_FIELDS: {
    ALL: (workspaceId: string) => `/workspaces/${workspaceId}/custom-fields`,
    ACTIVE: (workspaceId: string) => `/workspaces/${workspaceId}/custom-fields/active`,
    BY_ID: (customFieldId: string) => `/custom-fields/${customFieldId}`,
    REORDER: (workspaceId: string) => `/workspaces/${workspaceId}/custom-fields/reorder`,
    REQUIRED: (workspaceId: string, issueTypeId: string) => `/workspaces/${workspaceId}/custom-fields/required/${issueTypeId}`,
    TASK_VALUES: (taskId: string) => `/issues/${taskId}/custom-fields`,
    TASK_VALUE: (taskId: string, customFieldId: string) => `/issues/${taskId}/custom-fields/${customFieldId}`,
    VALIDATE: (customFieldId: string) => `/custom-fields/${customFieldId}/validate`,
  },

  // Labels
  LABELS: {
    ALL: (workspaceId: string) => `/workspaces/${workspaceId}/labels`,
    WITH_COUNTS: (workspaceId: string) => `/workspaces/${workspaceId}/labels/with-counts`,
    BY_ID: (labelId: string) => `/labels/${labelId}`,
    TASK_LABELS: (taskId: string) => `/issues/${taskId}/labels`,
    TASK_LABEL: (taskId: string, labelId: string) => `/issues/${taskId}/labels/${labelId}`,
    TASKS_WITH_LABEL: (labelId: string) => `/labels/${labelId}/tasks`,
    BY_TASKS: '/labels/by-tasks',
  },

  // Time Logs
  TIME_LOGS: {
    ALL: '/time-logs',
    BY_ID: (timeLogId: string) => `/time-logs/${timeLogId}`,
    TASK_LOGS: (taskId: string) => `/issues/${taskId}/time-logs`,
    TASK_SUMMARY: (taskId: string) => `/issues/${taskId}/time-logs/summary`,
    MY_LOGS: '/my/time-logs',
    MY_SUMMARY: '/my/time-logs/summary',
    PROJECT_LOGS: (projectId: string) => `/projects/${projectId}/time-logs`,
    PROJECT_SUMMARY: (projectId: string) => `/projects/${projectId}/time-logs/summary`,
    PROJECT_REPORT: (projectId: string) => `/projects/${projectId}/time-logs/report`,
    WORKSPACE_REPORT: (workspaceId: string) => `/workspaces/${workspaceId}/time-logs/report`,
    WORKSPACE_FILTERS: (workspaceId: string) => `/workspaces/${workspaceId}/report-filters`,
    DELETE_FILTER: (filterId: string) => `/report-filters/${filterId}`,
  },

  // GitHub
  GITHUB: {
    TASK_LINKS: (taskId: string) => `/issues/${taskId}/github`,
    BY_KEY: (issueKey: string) => `/github/by-key/${issueKey}`,
  },

  // Attachments
  ATTACHMENTS: {
    TASK: (taskId: string) => `/issues/${taskId}/attachments`,
    COMMENT: (commentId: number | string) => `/comments/${commentId}/attachments`,
    DOWNLOAD: (attachmentId: string) => `/attachments/${attachmentId}/download`,
    DELETE: (attachmentId: string) => `/attachments/${attachmentId}`,
  },

  // Analytics
  ANALYTICS: {
    DISTRIBUTION: (projectId: string) => `/projects/${projectId}/analytics/distribution`,
    WORKLOAD: (projectId: string) => `/projects/${projectId}/analytics/workload`,
    OVERDUE: (projectId: string) => `/projects/${projectId}/analytics/overdue`,
    THROUGHPUT: (projectId: string) => `/projects/${projectId}/analytics/throughput`,
    CYCLE_TIME: (projectId: string) => `/projects/${projectId}/analytics/cycle-time`,
    BOTTLENECKS: (projectId: string) => `/projects/${projectId}/analytics/bottlenecks`,
  },

  // Activity Log
  ACTIVITY: {
    TASK: (taskId: string) => `/issues/${taskId}/activity`,
    PROJECT: (projectId: string) => `/projects/${projectId}/activity`,
  },
} as const;

export default ENDPOINTS;
